import { GAME_CONSTANTS } from '../utils/Constants';
import { EconomyState, Enemy } from '../types/GameTypes';

export class EconomySystem {
  private state: EconomyState;

  constructor(initialGold: number = 150) {
    this.state = {
      gold: initialGold,
      gpm: GAME_CONSTANTS.GPM_BASE,
      killGoldTier: GAME_CONSTANTS.KILL_GOLD_BASE,
      goldFrozen: false,
    };
  }

  getState(): EconomyState {
    return { ...this.state };
  }

  getGold(): number {
    return this.state.gold;
  }

  canAfford(cost: number): boolean {
    return this.state.gold >= cost;
  }

  spend(cost: number): boolean {
    if (this.canAfford(cost)) {
      this.state.gold -= cost;
      return true;
    }
    return false;
  }

  addGold(amount: number): number {
    this.state.gold += amount;
    return amount; // Return amount added for logging
  }

  /**
   * Calculate kill gold based on wave number
   */
  calculateKillGold(wave: number): number {
    if (this.state.goldFrozen) {
      return GAME_CONSTANTS.KILL_GOLD_MAX;
    }

    const tier = Math.floor((wave - 1) / GAME_CONSTANTS.KILL_GOLD_TIER_INTERVAL);
    const killGold = Math.min(
      GAME_CONSTANTS.KILL_GOLD_BASE + tier,
      GAME_CONSTANTS.KILL_GOLD_MAX
    );
    
    this.state.killGoldTier = killGold;
    return killGold;
  }

  /**
   * Award gold for killing an enemy
   */
  awardKillGold(enemy: Enemy, wave: number): number {
    let gold = this.calculateKillGold(wave);

    // Elite enemies give 2x gold
    if (enemy.isElite) {
      gold *= 2;
    }

    // Ghost/Flying give 1.5x gold
    if (enemy.type === 'ghost' || enemy.type === 'flying') {
      gold = Math.floor(gold * 1.5);
    }

    this.addGold(gold);
    return gold; // Return gold awarded for logging
  }

  /**
   * Award wave completion bonus
   */
  awardWaveBonus(wave: number): number {
    let bonus: number = GAME_CONSTANTS.WAVE_BONUS_EARLY;
    
    if (wave >= 26 && wave <= 60) {
      bonus = GAME_CONSTANTS.WAVE_BONUS_MID;
    } else if (wave > 60) {
      bonus = GAME_CONSTANTS.WAVE_BONUS_LATE;
    }

    this.addGold(bonus);
    return bonus; // Return bonus amount for logging
  }

  /**
   * Award event wave bonus
   */
  awardEventBonus(): void {
    this.addGold(GAME_CONSTANTS.EVENT_BONUS);
  }

  /**
   * Award boss bonus
   */
  awardBossBonus(): void {
    this.addGold(GAME_CONSTANTS.BOSS_BONUS);
  }

  /**
   * Update GPM based on wave number
   */
  updateGPM(wave: number): void {
    if (this.state.goldFrozen) {
      return;
    }

    const intervals = Math.floor((wave - 1) / GAME_CONSTANTS.GPM_INCREASE_INTERVAL);
    this.state.gpm = Math.min(
      GAME_CONSTANTS.GPM_BASE + intervals * 2,
      GAME_CONSTANTS.GPM_MAX
    );
  }

  /**
   * Check and apply gold freeze after Wave 150
   */
  checkGoldFreeze(wave: number): void {
    if (wave >= GAME_CONSTANTS.GOLD_FREEZE_WAVE && !this.state.goldFrozen) {
      this.state.goldFrozen = true;
      this.state.killGoldTier = GAME_CONSTANTS.KILL_GOLD_MAX;
      this.state.gpm = GAME_CONSTANTS.GPM_MAX;
    }
  }

  /**
   * Update economy (call every frame for GPM)
   */
  update(deltaTime: number): void {
    if (!this.state.goldFrozen) {
      // GPM is per minute, so add gold every 60 seconds
      const goldPerSecond = this.state.gpm / 60;
      this.addGold(goldPerSecond * deltaTime);
    }
  }
}

